import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { TextInput, type RaRecord } from "react-admin";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useFormContext } from "react-hook-form";
import { itsAutoDefaultHelperText, useItsAutoLookup } from "@/components/its-lookup";

type RazaItsLookupProps = {
  source: string;
  label: string;
  fullWidth?: boolean;
  isRequired?: boolean;
};

type LookupHint =
  | { variant: "default" }
  | { variant: "notInDirectory"; its: string }
  | { variant: "lookupError"; message: string };

const defaultHelperText = itsAutoDefaultHelperText();

function hintToHelperNode(hint: LookupHint): ReactNode {
  if (hint.variant === "default") {
    return defaultHelperText;
  }
  if (hint.variant === "notInDirectory") {
    return (
      <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>
        ITS {hint.its} is not in directory - enter name and mohallah manually.
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ typography: "caption", color: "error.main" }}>
      {hint.message}
    </Box>
  );
}

export default function RazaItsLookup({
  source,
  label,
  fullWidth,
  isRequired,
}: RazaItsLookupProps) {
  const { setValue } = useFormContext();
  const [lookupHint, setLookupHint] = useState<LookupHint>({ variant: "default" });
  const prevItsRef = useRef<string | undefined>(undefined);
  const hasItsChangedRef = useRef(false);

  const onFound = useCallback(
    (row: RaRecord, t: string) => {
      setValue(source, row.ITS_ID ?? t);
      setValue("name", String(row.Full_Name ?? "").trim());
      setValue("mohallah", String(row.Jamaat ?? "").trim());
      setValue("itsDirectoryMatched", true);
      setValue("safaiUrl", null);
      setLookupHint({ variant: "default" });
    },
    [setValue, source]
  );

  const onNotFound = useCallback(
    (t: string) => {
      if (hasItsChangedRef.current) {
        setValue("name", "");
        setValue("mohallah", "");
      }
      setValue("itsDirectoryMatched", false);
      setLookupHint({ variant: "notInDirectory", its: t });
    },
    [setValue]
  );

  const onError = useCallback((e: unknown) => {
    setLookupHint({
      variant: "lookupError",
      message: e instanceof Error ? e.message : "ITS lookup failed",
    });
  }, []);

  const onIncomplete = useCallback(() => {
    setLookupHint({ variant: "default" });
    setValue("itsDirectoryMatched", undefined);
  }, [setValue]);

  const { loading, currentValue } = useItsAutoLookup({
    source,
    onFound,
    onNotFound,
    onError,
    onIncomplete,
  });

  useEffect(() => {
    const prevIts = prevItsRef.current;
    if (prevIts !== undefined && prevIts !== currentValue) {
      hasItsChangedRef.current = true;
    }
    prevItsRef.current = currentValue;
  }, [currentValue]);

  const helperText = useMemo(() => hintToHelperNode(lookupHint), [lookupHint]);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextInput
          source={source}
          label={label}
          fullWidth={fullWidth}
          isRequired={isRequired}
          helperText={helperText}
        />
      </Box>
      {loading ? <CircularProgress size={22} sx={{ mt: 2 }} /> : null}
    </Box>
  );
}
